import { useRef, useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Shield, Mic, Brain, FileText, Stethoscope, Chrome, Clock, ChevronRight, Check, Lock, Zap, Users, Activity, ArrowRight, Monitor, Square, Play } from 'lucide-react';

function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const TRANSCRIPT_LINES = [
  { speaker: 'Provider', text: 'Hello, how are you feeling today?' },
  { speaker: 'Patient', text: 'I\'ve been feeling really anxious lately, especially at work.' },
  { speaker: 'Provider', text: 'Can you tell me more about what triggers the anxiety?' },
  { speaker: 'Patient', text: 'Mostly deadlines and presentations. I have trouble sleeping the night before.' },
  { speaker: 'Provider', text: 'How long has this been going on?' },
  { speaker: 'Patient', text: 'About three months now. It\'s gotten worse since my promotion.' },
];

const NOTE_SECTIONS = [
  { key: 'Dx', label: 'Diagnosis (DSM-5)', text: 'Generalized Anxiety Disorder (F41.1) — moderate. Insomnia disorder (G47.00). R/O Adjustment Disorder with anxiety. Biopsychosocial: occupational stressor (promotion), sleep disruption, no substance use.', color: '#C47A5E' },
  { key: 'S', label: 'Subjective', text: 'Patient reports increased anxiety triggered by work deadlines and presentations. Difficulty sleeping night before high-pressure events. Onset ~3 months, worsened after promotion.', color: '#2C4A3B' },
  { key: 'O', label: 'Objective', text: 'Mood: Anxious. Affect: Mildly distressed, congruent. Cooperative throughout. No psychomotor agitation. Speech normal rate/volume.', color: '#2C4A3B' },
  { key: 'A', label: 'Assessment', text: 'GAD (F41.1) moderate severity. Insomnia (G47.00). Occupational stress as primary precipitant. GAD-7 score: 14 (moderate).', color: '#2C4A3B' },
  { key: 'P', label: 'Plan', text: 'Continue weekly CBT. Introduce sleep hygiene protocol. GAD-7 reassessment in 4 weeks. Consider psychiatry referral if no improvement.', color: '#2C4A3B' },
  { key: 'CPT', label: 'Billing Codes', text: 'CPT: 90834 (Psychotherapy 45min) | ICD-10: F41.1, G47.00 | E/M: 99213', color: '#6B8E7B' },
];

const EHR_PUSH_FIELDS = [
  { field: 'Subjective', status: 'done' },
  { field: 'Objective', status: 'done' },
  { field: 'Assessment', status: 'done' },
  { field: 'Plan', status: 'done' },
  { field: 'Diagnosis Codes (ICD-10)', status: 'done' },
  { field: 'Billing Codes (CPT)', status: 'done' },
];

const PHASE_DURATION = { recording: 5000, transcribing: 4500, generating: 5500, pushing: 4000 };

function LiveDemoWalkthrough() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const [phase, setPhase] = useState('idle');
  const [visibleLines, setVisibleLines] = useState(0);
  const [visibleNotes, setVisibleNotes] = useState(0);
  const [visiblePush, setVisiblePush] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [waveform, setWaveform] = useState(Array(24).fill(3));
  const [detectedEhr, setDetectedEhr] = useState('');
  const [expandedNote, setExpandedNote] = useState(null);
  const [viewPhase, setViewPhase] = useState(null); // null = follow live, or 'recording'|'transcribing'|'generating'|'pushing'
  const intervalRef = useRef(null);

  useEffect(() => {
    if (inView && phase === 'idle') {
      const timer = setTimeout(() => setPhase('recording'), 600);
      return () => clearTimeout(timer);
    }
  }, [inView, phase]);

  useEffect(() => {
    if (phase === 'recording') {
      setElapsed(0); setVisibleLines(0); setVisibleNotes(0); setVisiblePush(0); setDetectedEhr('');
      intervalRef.current = setInterval(() => {
        setWaveform(Array(24).fill(0).map(() => Math.random() * 20 + 3));
      }, 150);
      const timer = setTimeout(() => {
        clearInterval(intervalRef.current);
        setWaveform(Array(24).fill(3));
        setPhase('transcribing');
      }, PHASE_DURATION.recording);
      const counter = setInterval(() => setElapsed(e => e + 1), 1000);
      return () => { clearTimeout(timer); clearInterval(counter); clearInterval(intervalRef.current); };
    }
    if (phase === 'transcribing') {
      let line = 0;
      const timer = setInterval(() => {
        line++;
        setVisibleLines(line);
        if (line >= TRANSCRIPT_LINES.length) {
          clearInterval(timer);
          setTimeout(() => setPhase('generating'), 600);
        }
      }, PHASE_DURATION.transcribing / TRANSCRIPT_LINES.length);
      return () => clearInterval(timer);
    }
    if (phase === 'generating') {
      let s = 0;
      const timer = setInterval(() => {
        s++;
        setVisibleNotes(s);
        if (s >= NOTE_SECTIONS.length) {
          clearInterval(timer);
          setTimeout(() => setPhase('pushing'), 800);
        }
      }, PHASE_DURATION.generating / NOTE_SECTIONS.length);
      return () => clearInterval(timer);
    }
    if (phase === 'pushing') {
      setDetectedEhr('');
      // Step 1: detect EHR
      const detectTimer = setTimeout(() => setDetectedEhr('SimplePractice'), 800);
      // Step 2: push fields one by one
      let f = 0;
      const pushTimer = setInterval(() => {
        f++;
        setVisiblePush(f);
        if (f >= EHR_PUSH_FIELDS.length) {
          clearInterval(pushTimer);
          setTimeout(() => setPhase('done'), 800);
        }
      }, (PHASE_DURATION.pushing - 1200) / EHR_PUSH_FIELDS.length);
      return () => { clearTimeout(detectTimer); clearInterval(pushTimer); };
    }
  }, [phase]);

  const restart = () => {
    setPhase('idle'); setVisibleLines(0); setVisibleNotes(0); setVisiblePush(0); setElapsed(0); setDetectedEhr(''); setExpandedNote(null); setViewPhase(null);
    setTimeout(() => setPhase('recording'), 300);
  };

  const PHASES = ['Recording', 'Transcribing', 'Diagnosis & Notes', 'Push to EHR'];
  const PHASE_KEYS = ['recording', 'transcribing', 'generating', 'pushing'];
  const phaseIndex = phase === 'recording' ? 0 : phase === 'transcribing' ? 1 : phase === 'generating' ? 2 : phase === 'pushing' || phase === 'done' ? 3 : -1;
  const viewIndex = viewPhase ? PHASE_KEYS.indexOf(viewPhase) : -1;
  // What the right panel shows: the user-selected view phase, or the live phase
  const displayPhase = viewPhase || phase;

  const handlePhaseClick = (i) => {
    // Only allow clicking on completed phases or current phase
    if (i <= phaseIndex) {
      setViewPhase(PHASE_KEYS[i]);
      setExpandedNote(null);
    }
  };

  return (
    <div ref={ref} className="bg-[#1C1917] rounded-3xl overflow-hidden shadow-2xl border border-stone-800" data-testid="demo-walkthrough">
      {/* Phase bar — clickable */}
      <div className="flex items-center gap-0 border-b border-stone-800">
        {PHASES.map((label, i) => {
          const isClickable = i <= phaseIndex;
          const isViewing = viewPhase ? viewIndex === i : phaseIndex === i;
          return (
            <div
              key={i}
              onClick={() => isClickable && handlePhaseClick(i)}
              className={`flex-1 py-3 px-3 text-center text-[11px] font-semibold tracking-wide transition-colors duration-300 ${
                isViewing ? 'bg-[#2C4A3B] text-white' : phaseIndex > i ? 'bg-[#2C4A3B]/30 text-stone-400' : phaseIndex === i ? 'bg-[#2C4A3B] text-white' : 'text-stone-600'
              } ${isClickable ? 'cursor-pointer hover:brightness-110' : ''}`}
              data-testid={`demo-phase-${i}`}
            >
              <span className={`inline-block w-4 h-4 rounded-full text-[10px] leading-4 mr-1 ${phaseIndex > i ? 'bg-green-500 text-white' : phaseIndex === i ? 'bg-[#C47A5E] text-white' : 'bg-stone-700 text-stone-500'}`}>
                {phaseIndex > i ? '\u2713' : i + 1}
              </span>
              {label}
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 min-h-[420px]">
        {/* Left panel */}
        <div className="p-6 md:p-8 border-r border-stone-800/60 flex flex-col">
          <AnimatePresence mode="wait">
            {(phase === 'recording' || phase === 'idle') && (
              <motion.div key="rec" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center flex-1 gap-5">
                <div className="relative">
                  <motion.div
                    animate={phase === 'recording' ? { scale: [1, 1.25, 1] } : {}}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center"
                  >
                    <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center">
                      <Mic className="w-6 h-6 text-red-400" />
                    </div>
                  </motion.div>
                  {phase === 'recording' && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-white animate-pulse" />
                    </motion.div>
                  )}
                </div>
                {phase === 'recording' && (
                  <>
                    <p className="text-xs font-semibold text-red-400 uppercase tracking-widest">Recording Session</p>
                    <p className="text-2xl font-bold text-white tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>0:{elapsed.toString().padStart(2, '0')}</p>
                    <div className="flex items-end gap-[3px] h-8">
                      {waveform.map((h, i) => (
                        <motion.div key={i} className="w-[3px] rounded-full bg-red-400/60" animate={{ height: h }} transition={{ duration: 0.1 }} />
                      ))}
                    </div>
                  </>
                )}
                {phase === 'idle' && <p className="text-xs text-stone-500">Starting demo...</p>}
              </motion.div>
            )}

            {(phase === 'transcribing' || phase === 'generating' || phase === 'pushing' || phase === 'done') && (
              <motion.div key="trans" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <p className="text-xs font-semibold text-green-400 uppercase tracking-widest">Transcript — Speaker Diarization</p>
                </div>
                <div className="space-y-2 max-h-[340px] overflow-y-auto pr-2">
                  {TRANSCRIPT_LINES.slice(0, visibleLines).map((line, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="flex gap-3">
                      <span className={`text-[10px] font-bold uppercase tracking-wider shrink-0 w-16 pt-0.5 ${line.speaker === 'Provider' ? 'text-[#C47A5E]' : 'text-sky-400'}`}>{line.speaker}</span>
                      <p className="text-sm text-stone-300 leading-relaxed">{line.text}</p>
                    </motion.div>
                  ))}
                  {phase === 'transcribing' && visibleLines < TRANSCRIPT_LINES.length && (
                    <div className="flex gap-3 items-center">
                      <span className="w-16" />
                      <span className="flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-stone-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-stone-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-stone-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right panel */}
        <div className="p-6 md:p-8 bg-[#141211] flex flex-col">
          <AnimatePresence mode="wait">
            {/* Waiting state */}
            {(displayPhase === 'idle' || displayPhase === 'recording' || displayPhase === 'transcribing') && visibleNotes === 0 && !viewPhase && (
              <motion.div key="wait" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center flex-1 gap-3 text-center">
                <div className="w-14 h-14 rounded-2xl bg-stone-800 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-stone-600" />
                </div>
                <p className="text-xs text-stone-600 max-w-[200px]">
                  {displayPhase === 'transcribing' ? 'AI will generate diagnosis & notes once transcription completes...' : 'Diagnosis & clinical notes will appear here...'}
                </p>
              </motion.div>
            )}

            {/* Viewing completed recording phase */}
            {viewPhase === 'recording' && phaseIndex > 0 && (
              <motion.div key="view-rec" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center flex-1 gap-4 text-center">
                <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-400" />
                </div>
                <p className="text-sm font-semibold text-stone-300" style={{ fontFamily: "'Outfit', sans-serif" }}>Recording Complete</p>
                <p className="text-xs text-stone-500 max-w-[240px]">Session audio captured with real-time chunking. Speaker diarization will label Provider vs Patient turns.</p>
              </motion.div>
            )}

            {/* Viewing completed transcription phase */}
            {viewPhase === 'transcribing' && phaseIndex > 1 && (
              <motion.div key="view-trans" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center flex-1 gap-4 text-center">
                <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-400" />
                </div>
                <p className="text-sm font-semibold text-stone-300" style={{ fontFamily: "'Outfit', sans-serif" }}>Transcription Complete</p>
                <p className="text-xs text-stone-500 max-w-[240px]">{TRANSCRIPT_LINES.length} conversation turns captured with speaker diarization (Provider / Patient labels).</p>
              </motion.div>
            )}

            {/* Generating notes OR viewing completed notes */}
            {(displayPhase === 'generating' || (viewPhase === 'generating' && phaseIndex >= 2)) && (
              <motion.div key="gen" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1">
                <div className="flex items-center gap-2 mb-4">
                  {phaseIndex > 2 || viewPhase === 'generating' ? (
                    <Check className="w-3.5 h-3.5 text-green-500" />
                  ) : (
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-3.5 h-3.5 border-2 border-[#C47A5E] border-t-transparent rounded-full" />
                  )}
                  <p className="text-xs font-semibold text-[#C47A5E] uppercase tracking-widest">
                    {phaseIndex > 2 || viewPhase === 'generating' ? 'Diagnosis & Notes — Click to expand' : 'Generating Diagnosis & Notes...'}
                  </p>
                </div>
                <div className="space-y-2 max-h-[360px] overflow-y-auto pr-2">
                  {(phaseIndex > 2 || viewPhase === 'generating' ? NOTE_SECTIONS : NOTE_SECTIONS.slice(0, visibleNotes)).map((sec) => (
                    <motion.div
                      key={sec.key}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                      className="bg-stone-800/50 rounded-xl border border-stone-700/50 overflow-hidden cursor-pointer hover:border-stone-600 transition-colors"
                      onClick={() => setExpandedNote(expandedNote === sec.key ? null : sec.key)}
                      data-testid={`demo-note-${sec.key}`}
                    >
                      <div className="flex items-center gap-2 p-3">
                        <span className="w-6 h-6 rounded-md text-white text-[9px] font-bold flex items-center justify-center shrink-0" style={{ background: sec.color }}>{sec.key}</span>
                        <span className="text-[11px] font-bold text-stone-300 flex-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{sec.label}</span>
                        <ChevronRight className={`w-3 h-3 text-stone-500 transition-transform ${expandedNote === sec.key ? 'rotate-90' : ''}`} />
                      </div>
                      <AnimatePresence>
                        {expandedNote === sec.key ? (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.2 }}>
                            <p className="text-[11px] text-stone-400 leading-relaxed px-3 pb-3">{sec.text}</p>
                          </motion.div>
                        ) : (
                          <p className="text-[11px] text-stone-500 px-3 pb-2 truncate">{sec.text.substring(0, 60)}...</p>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                  {!viewPhase && phase === 'generating' && visibleNotes < NOTE_SECTIONS.length && (
                    <div className="flex items-center gap-2 px-3 py-2">
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-3 h-3 border-2 border-stone-600 border-t-stone-400 rounded-full" />
                      <span className="text-[11px] text-stone-600">Generating...</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* Pushing to EHR OR viewing push results */}
            {((displayPhase === 'pushing' || displayPhase === 'done') && viewPhase !== 'recording' && viewPhase !== 'transcribing' && viewPhase !== 'generating') && (
              <motion.div key="push" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex-1">
                <div className="flex items-center gap-2 mb-4">
                  {phase === 'done' ? (
                    <Check className="w-3.5 h-3.5 text-green-500" />
                  ) : (
                    <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-3.5 h-3.5 border-2 border-green-400 border-t-transparent rounded-full" />
                  )}
                  <p className="text-xs font-semibold text-green-400 uppercase tracking-widest">
                    {phase === 'done' ? 'Pushed to EHR' : 'Pushing to EHR...'}
                  </p>
                </div>

                {/* EHR detection */}
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="bg-stone-800/60 rounded-xl p-3 mb-3 border border-stone-700/40 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-600 flex items-center justify-center text-white text-[9px] font-extrabold shrink-0">SP</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold text-stone-200">
                      {detectedEhr ? `${detectedEhr} Detected` : 'Scanning for EHR...'}
                    </p>
                    <p className="text-[10px] text-stone-500">secure.simplepractice.com</p>
                  </div>
                  {detectedEhr && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-green-400" />
                    </motion.div>
                  )}
                </motion.div>

                {/* Push progress */}
                <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-2">
                  {EHR_PUSH_FIELDS.slice(0, visiblePush).map((item, i) => (
                    <motion.div key={i} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.25 }} className="flex items-center gap-2.5 bg-stone-800/30 rounded-lg px-3 py-2 border border-stone-800">
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.1 }} className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center shrink-0">
                        <Check className="w-2.5 h-2.5 text-white" />
                      </motion.div>
                      <span className="text-[11px] text-stone-300 flex-1">{item.field}</span>
                      <span className="text-[9px] text-green-400 font-semibold uppercase tracking-wider">Pushed</span>
                    </motion.div>
                  ))}
                  {phase === 'pushing' && visiblePush < EHR_PUSH_FIELDS.length && (
                    <div className="flex items-center gap-2.5 px-3 py-2">
                      <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }} className="w-3.5 h-3.5 border-2 border-stone-600 border-t-green-400 rounded-full" />
                      <span className="text-[11px] text-stone-600">Pushing sections...</span>
                    </div>
                  )}
                </div>

                {/* Success banner */}
                {(phase === 'done' && !viewPhase) && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-3 bg-green-500/10 border border-green-500/20 rounded-xl px-4 py-3" data-testid="demo-push-success">
                    <div className="flex items-center gap-3 mb-2">
                      <Check className="w-4 h-4 text-green-400 shrink-0" />
                      <div>
                        <p className="text-[11px] font-semibold text-green-300">All sections pushed to SimplePractice</p>
                        <p className="text-[10px] text-green-400/60">6 fields &middot; 0 errors &middot; via browser extension</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-stone-500 mt-1">Click <strong className="text-stone-400">Diagnosis & Notes</strong> tab above to review all generated content.</p>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-stone-800 px-6 py-3 flex items-center justify-between bg-[#1C1917]">
        <div className="flex items-center gap-4">
          <span className="text-[10px] uppercase tracking-wider text-stone-600 font-semibold">Live Demo</span>
          {phase === 'done' && (
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] text-green-400 font-semibold flex items-center gap-1">
              <Check className="w-3 h-3" /> Recorded, transcribed, diagnosed, notes generated & pushed to EHR
            </motion.span>
          )}
        </div>
        {phase === 'done' && (
          <button onClick={restart} className="text-xs font-semibold text-[#C47A5E] hover:text-white transition-colors flex items-center gap-1" data-testid="demo-replay">
            <Play className="w-3 h-3" /> Replay
          </button>
        )}
      </div>
    </div>
  );
}

const TEMPLATES = [
  { name: 'CBT', desc: 'Cognitive Behavioral Therapy', icon: Brain },
  { name: 'DBT', desc: 'Dialectical Behavior Therapy', icon: Activity },
  { name: 'Intake', desc: 'Initial Assessment', icon: FileText },
  { name: 'Med Management', desc: 'Medication Review', icon: Stethoscope },
  { name: 'EMDR', desc: 'Eye Movement Desensitization', icon: Zap },
  { name: 'Crisis', desc: 'Crisis Intervention', icon: Shield },
  { name: 'Grief', desc: 'Grief & Bereavement', icon: Users },
  { name: 'Group', desc: 'Group Therapy Session', icon: Users },
];

const EHR_SYSTEMS = ['Epic', 'Cerner', 'Tebra', 'Athenahealth', 'DrChrono', 'SimplePractice', 'TherapyNotes', 'Valant', 'AdvancedMD', 'NextGen', 'Allscripts', 'Practice Fusion', 'ICANotes', 'Elation', 'eClinicalWorks'];

export default function LandingPage() {
  const navigate = useNavigate();
  const goTrial = () => navigate('/register');

  return (
    <div className="min-h-screen" style={{ fontFamily: "'Manrope', sans-serif", background: '#F9F6F0' }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Manrope:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* ─── HEADER ──────────────────────────────── */}
      <header className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-xl border-b border-stone-200/60" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-[#2C4A3B] flex items-center justify-center">
              <Mic className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-[#1C1917] tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Plexura AI Scribe
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            {['Features', 'Templates', 'Security', 'Pricing'].map(link => (
              <a key={link} href={`#${link.toLowerCase()}`} className="text-sm font-medium text-stone-500 hover:text-[#2C4A3B] transition-colors" data-testid={`nav-${link.toLowerCase()}`}>
                {link}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="text-sm font-medium text-stone-600 hover:text-[#2C4A3B] transition-colors hidden sm:block" data-testid="nav-login">
              Sign In
            </button>
            <button onClick={goTrial} className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#2C4A3B] text-white hover:bg-[#1E3329] transition-all shadow-sm hover:-translate-y-0.5 hover:shadow-md" data-testid="nav-cta">
              Start Free Trial
            </button>
          </div>
        </div>
      </header>

      {/* ─── HERO ────────────────────────────────── */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <FadeIn>
              <div className="inline-flex items-center rounded-full bg-[#E3E8E4] px-3.5 py-1.5 text-xs font-semibold text-[#2C4A3B] ring-1 ring-inset ring-[#6B8E7B]/20 mb-6 tracking-wide uppercase">
                <Shield className="w-3 h-3 mr-1.5" />
                HIPAA Compliant
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-[#1C1917] tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Clinical Documentation That <span className="text-[#2C4A3B]">Listens</span>, <span className="text-[#C47A5E]">Codes</span>, and <span className="text-[#2C4A3B]">Connects</span>
              </h1>
              <p className="text-lg text-stone-500 leading-relaxed mb-8 max-w-lg">
                Reduce documentation burden for mental/behavioral health providers. AI-powered session recording, transcription, and note generation — so you can focus on your patients.
              </p>
              <div className="flex flex-wrap gap-3">
                <button onClick={goTrial} className="rounded-full px-7 py-3.5 text-sm font-semibold bg-[#2C4A3B] text-white hover:bg-[#1E3329] transition-all shadow-md hover:-translate-y-1 hover:shadow-lg flex items-center gap-2" data-testid="hero-cta-trial">
                  Start 7-Day Free Trial
                  <ArrowRight className="w-4 h-4" />
                </button>
                <a href="#features" className="rounded-full px-7 py-3.5 text-sm font-semibold text-[#2C4A3B] bg-white border border-stone-200 hover:border-[#2C4A3B] transition-all hover:-translate-y-1 hover:shadow-sm" data-testid="hero-cta-learn">
                  See How It Works
                </a>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <div className="relative">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/b1fb24c2-8043-4dfe-9dcb-4d18962a412b/images/de2f23cf0adf05b7bbd9cbfe7f9e763d36f99a4cc30bc1e498d045c7ca0c568b.png"
                  alt="Plexura AI Scribe Dashboard"
                  className="rounded-3xl shadow-2xl border border-stone-200/50 w-full"
                  data-testid="hero-image"
                />
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-lg border border-stone-100 px-4 py-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center"><Check className="w-4 h-4 text-green-600" /></div>
                  <div>
                    <p className="text-xs font-semibold text-stone-800">SOAP Note Generated</p>
                    <p className="text-[10px] text-stone-400">Automatically in 12 seconds</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── IMPACT STATS ────────────────────────── */}
      <section className="py-16 md:py-20 border-y border-stone-200/60 bg-white/40" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-16">
            {[
              { value: '2+ hrs', label: 'Saved per day on documentation', accent: '#2C4A3B' },
              { value: '16+', label: 'EHR systems supported natively', accent: '#C47A5E' },
              { value: '100%', label: 'HIPAA compliant', accent: '#2C4A3B' },
            ].map((stat, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="text-center" data-testid={`stat-${i}`}>
                  <p className="text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: stat.accent }}>{stat.value}</p>
                  <p className="text-sm text-stone-500 font-medium">{stat.label}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CLINICAL PHOTO: THERAPY SESSION ─────── */}
      <FadeIn>
        <section className="relative overflow-hidden" data-testid="clinical-photo-therapy">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="rounded-3xl overflow-hidden relative" style={{ height: '360px' }}>
              <img
                src="https://images.unsplash.com/photo-1714976695024-55a90b113f68?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHx0aGVyYXBpc3QlMjBwYXRpZW50JTIwc2Vzc2lvbiUyMGNsaW5pY2FsJTIwbWVudGFsJTIwaGVhbHRoJTIwY291bnNlbGluZ3xlbnwwfHx8fDE3NzYzMzA4ODZ8MA&ixlib=rb-4.1.0&q=85&w=1400"
                alt="Mental health practitioner in a counseling session with patient"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1C1917]/70 via-[#1C1917]/30 to-transparent" />
              <div className="absolute bottom-0 left-0 p-8 md:p-12 max-w-lg">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C47A5E] mb-2">Focus on What Matters</p>
                <p className="text-xl md:text-2xl font-bold text-white leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Be present with your patients. Let AI handle the documentation.
                </p>
              </div>
            </div>
          </div>
        </section>
      </FadeIn>

      {/* ─── HOW IT WORKS ────────────────────────── */}
      <section id="features" className="py-24 md:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C47A5E] mb-3">How It Works</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C1917] tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              From Session to Notes in Minutes
            </h2>
            <p className="text-lg text-stone-500 max-w-2xl mb-16">Three simple steps to eliminate documentation burden and get back to what matters — your patients.</p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Record Your Session', desc: 'Start recording with one click. Our system captures 60+ minute sessions with real-time audio chunking and automatic EST timestamps.', icon: Mic, color: '#2C4A3B' },
              { step: '02', title: 'AI Transcribes & Generates', desc: 'Speaker diarization labels Provider vs Patient. AI auto-generates comprehensive diagnosis, SOAP notes, psychotherapy notes, and billing codes.', icon: Brain, color: '#C47A5E' },
              { step: '03', title: 'Push to Any EHR', desc: 'Use our browser extension to push notes directly into Epic, Cerner, Tebra, SimplePractice, or any EHR. One click, section-by-section or full note.', icon: Monitor, color: '#2C4A3B' },
            ].map((item, i) => (
              <FadeIn key={i} delay={i * 0.12}>
                <div className="bg-white rounded-3xl p-8 md:p-10 border border-stone-200 hover:shadow-lg transition-shadow group" data-testid={`step-${i}`}>
                  <div className="flex items-center justify-between mb-8">
                    <span className="text-5xl font-light text-stone-200 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.step}</span>
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center transition-colors" style={{ background: `${item.color}15` }}>
                      <item.icon className="w-5 h-5" style={{ color: item.color }} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#1C1917] mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{item.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* ─── ANIMATED WALKTHROUGH ─── */}
          <FadeIn>
            <div className="mt-20">
              <LiveDemoWalkthrough />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ─── FEATURES BENTO GRID ─────────────────── */}
      <section className="py-24 md:py-32 bg-white" data-testid="features-bento-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C47A5E] mb-3">Core Capabilities</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C1917] tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Comprehensive AI Across the Encounter
            </h2>
            <p className="text-lg text-stone-500 max-w-2xl mb-16">Every feature designed to support mental/behavioral health documentation from start to finish.</p>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {/* Large card */}
            <FadeIn className="md:col-span-2 md:row-span-2">
              <div className="bg-[#2C4A3B] rounded-3xl p-8 md:p-10 h-full flex flex-col justify-between text-white" data-testid="feature-transcription">
                <div>
                  <Mic className="w-8 h-8 mb-6 text-[#C47A5E]" />
                  <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>Real-Time Transcription</h3>
                  <p className="text-stone-300 text-sm leading-relaxed">Record sessions of any length. Audio is chunked and transcribed in real-time using OpenAI Whisper, with automatic speaker diarization labeling Provider and Patient turns.</p>
                </div>
                <img src="https://static.prod-images.emergentagent.com/jobs/b1fb24c2-8043-4dfe-9dcb-4d18962a412b/images/1c5f18fecfa99c3f43326ae949d0b5d0434367093d60df9e12e320757d22b3ee.png" alt="Transcription" className="rounded-2xl mt-6 w-full opacity-80" />
              </div>
            </FadeIn>
            {/* Small cards */}
            {[
              { title: 'SOAP Medical Notes', desc: 'Auto-generated Subjective, Objective, Assessment, Plan with treatment recommendations.', icon: FileText, testId: 'feature-soap' },
              { title: 'ICD-10 & CPT Codes', desc: 'Automatic billing codes including E/M and add-on codes matched to session templates.', icon: Zap, testId: 'feature-billing' },
              { title: 'Comprehensive Diagnosis', desc: 'DSM-5 aligned diagnostic evaluation with differential diagnosis and biopsychosocial formulation.', icon: Stethoscope, testId: 'feature-diagnosis' },
              { title: 'Psychotherapy Notes', desc: 'Detailed therapy notes with interventions, client response, goals, and risk assessment.', icon: Brain, testId: 'feature-psych' },
            ].map((f, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div className="bg-[#F9F6F0] rounded-3xl p-7 border border-stone-200 h-full hover:shadow-md transition-shadow" data-testid={f.testId}>
                  <f.icon className="w-6 h-6 text-[#2C4A3B] mb-4" />
                  <h3 className="text-base font-bold text-[#1C1917] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{f.title}</h3>
                  <p className="text-xs text-stone-500 leading-relaxed">{f.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SESSION TEMPLATES ───────────────────── */}
      <section id="templates" className="py-24 md:py-32 bg-[#2C4A3B]" data-testid="templates-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C47A5E] mb-3">Session Templates</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Built for Every Therapy Style
            </h2>
            <p className="text-base text-stone-300 max-w-2xl mb-16">8 specialized templates that automatically configure documentation structure, billing codes, and clinical focus.</p>
          </FadeIn>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {TEMPLATES.map((t, i) => (
              <FadeIn key={i} delay={i * 0.06}>
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:bg-white/15 transition-colors group" data-testid={`template-${t.name.toLowerCase().replace(/\s/g, '-')}`}>
                  <t.icon className="w-5 h-5 text-[#C47A5E] mb-3 group-hover:scale-110 transition-transform" />
                  <p className="text-sm font-bold text-white mb-0.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.name}</p>
                  <p className="text-xs text-stone-400">{t.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CLINICAL PHOTO: DOCTOR CONSULTATION ──── */}
      <FadeIn>
        <section className="relative overflow-hidden" data-testid="clinical-photo-doctor">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="rounded-3xl overflow-hidden relative" style={{ height: '360px' }}>
              <img
                src="https://images.unsplash.com/photo-1675270714610-11a5cadcc7b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxkb2N0b3IlMjBwYXRpZW50JTIwZmFjZSUyMHRvJTIwZmFjZSUyMGNvbnN1bHRhdGlvbiUyMG9mZmljZSUyMGZ1bGwlMjBib2R5fGVufDB8fHx8MTc3NjMzMTI3NHww&ixlib=rb-4.1.0&q=85&w=1400"
                alt="Doctor consulting with patient in a medical office"
                className="w-full h-full object-cover" style={{ objectPosition: 'center 35%' }}
              />
              <div className="absolute inset-0 bg-gradient-to-l from-[#1C1917]/70 via-[#1C1917]/30 to-transparent" />
              <div className="absolute bottom-0 right-0 p-8 md:p-12 max-w-lg text-right">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C47A5E] mb-2">Works Across Disciplines</p>
                <p className="text-xl md:text-2xl font-bold text-white leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  From psychiatry to primary care — one platform for every clinical conversation.
                </p>
              </div>
            </div>
          </div>
        </section>
      </FadeIn>

      {/* ─── EHR INTEGRATION ─────────────────────── */}
      <section className="py-24 md:py-32" data-testid="ehr-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C47A5E] mb-3">Universal EHR Integration</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1C1917] tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Push Notes to Any EHR System
              </h2>
              <p className="text-base text-stone-500 leading-relaxed mb-8">
                Our browser extension for Chrome, Edge, and Safari auto-detects your EHR and enables one-click note push. Section-by-section paste, floating sidebar, or full-note injection.
              </p>
              <div className="flex flex-wrap gap-2 mb-8">
                {EHR_SYSTEMS.slice(0, 10).map(ehr => (
                  <span key={ehr} className="inline-flex items-center rounded-full bg-[#E3E8E4] px-3 py-1.5 text-xs font-medium text-[#2C4A3B]">{ehr}</span>
                ))}
                <span className="inline-flex items-center rounded-full bg-stone-100 px-3 py-1.5 text-xs font-medium text-stone-500">+5 more</span>
              </div>
              <div className="flex gap-3">
                <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-stone-200">
                  <Chrome className="w-5 h-5 text-[#2C4A3B]" />
                  <span className="text-xs font-semibold text-stone-700">Chrome</span>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-stone-200">
                  <Monitor className="w-5 h-5 text-[#2C4A3B]" />
                  <span className="text-xs font-semibold text-stone-700">Edge</span>
                </div>
                <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-stone-200">
                  <svg className="w-5 h-5 text-[#2C4A3B]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  <span className="text-xs font-semibold text-stone-700">Safari</span>
                </div>
              </div>
            </FadeIn>
            <FadeIn delay={0.15}>
              <img
                src="https://static.prod-images.emergentagent.com/jobs/b1fb24c2-8043-4dfe-9dcb-4d18962a412b/images/177917a806f77b9320f68cfec668dc8d13570aede19c7432bdaeaba37afdf601.png"
                alt="EHR Extension"
                className="rounded-3xl shadow-xl border border-stone-200/50 w-full"
                data-testid="ehr-image"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── SECURITY & HIPAA ────────────────────── */}
      <section id="security" className="py-24 md:py-32 bg-white" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <img
                src="https://images.unsplash.com/photo-1758273241260-f49172d876e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHx0aGVyYXBpc3QlMjBwYXRpZW50JTIwc2Vzc2lvbnxlbnwwfHx8fDE3NzYzMjgyNjB8MA&ixlib=rb-4.1.0&q=85&w=800"
                alt="Secure therapy session"
                className="rounded-3xl shadow-lg w-full object-cover aspect-[4/3]"
                data-testid="security-image"
              />
            </FadeIn>
            <FadeIn delay={0.12}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C47A5E] mb-3">Trust & Security</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1C1917] tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Built for Clinical Privacy
              </h2>
              <div className="space-y-5">
                {[
                  { title: 'HIPAA Compliant', desc: 'End-to-end encryption for all session data, transcripts, and clinical notes.', icon: Shield },
                  { title: 'Secure Recording', desc: 'Audio is processed in real-time and never stored permanently on our servers.', icon: Lock },
                  { title: 'Patient Consent Tracking', desc: 'Built-in consent management before any session recording begins.', icon: Check },
                  { title: 'Audit Logging', desc: 'Complete audit trail of all data access, modifications, and EHR pushes.', icon: FileText },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4" data-testid={`security-feature-${i}`}>
                    <div className="w-10 h-10 rounded-xl bg-[#E3E8E4] flex items-center justify-center shrink-0">
                      <item.icon className="w-4 h-4 text-[#2C4A3B]" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</p>
                      <p className="text-xs text-stone-500 mt-0.5">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── PRICING ─────────────────────────────── */}
      <section id="pricing" className="py-24 md:py-32" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="text-center mb-16">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C47A5E] mb-3">Simple Pricing</p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C1917] tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Choose the plan that fits your practice.
              </h2>
              <p className="text-base text-stone-500 max-w-lg mx-auto">Start with a free 7-day trial. No credit card required. Cancel anytime.</p>
            </div>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto items-stretch">
            {/* Professional card */}
            <FadeIn delay={0.05}>
              <div className="h-full bg-white rounded-3xl border-2 border-[#C47A5E]/30 p-8 md:p-10 shadow-lg flex flex-col" data-testid="pricing-card">
                <div className="text-center mb-8">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#C47A5E] mb-4">Professional</p>
                  <p className="text-xs text-stone-500 mb-4">For solo clinicians and small practices</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>$79</span>
                    <span className="text-2xl font-bold text-stone-400">.99</span>
                    <span className="text-sm text-stone-400 ml-1">/month</span>
                  </div>
                  <p className="text-xs text-[#2C4A3B] font-medium mt-2 bg-[#E3E8E4] inline-block px-3 py-1 rounded-full">7-day free trial included</p>
                </div>
                <div className="space-y-3 mb-8 flex-1">
                  {[
                    'Real-time session transcription',
                    'AI speaker diarization',
                    'Comprehensive DSM-5 diagnosis',
                    'SOAP medical notes (auto-generated)',
                    'Psychotherapy progress notes',
                    'ICD-10, CPT, E/M billing codes',
                    '8 session templates',
                    'Push to any EHR system',
                    'Universal browser extension (Chrome, Edge, Safari)',
                    'FHIR & CCD exports',
                    'Session timing (EST)',
                    'HIPAA compliant',
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <Check className="w-4 h-4 text-[#2C4A3B] shrink-0" />
                      <span className="text-sm text-stone-600">{feature}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={goTrial}
                  className="w-full rounded-full py-3.5 text-sm font-semibold bg-[#2C4A3B] text-white hover:bg-[#1E3329] transition-all shadow-md hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
                  data-testid="pricing-cta"
                >
                  Start Free Trial
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </FadeIn>

            {/* Enterprise card */}
            <FadeIn delay={0.15}>
              <div
                className="h-full rounded-3xl p-8 md:p-10 shadow-xl flex flex-col relative overflow-hidden text-white"
                style={{
                  background: 'linear-gradient(155deg,#1E3329 0%,#2C4A3B 55%,#37624E 100%)',
                }}
                data-testid="pricing-card-enterprise"
              >
                <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full opacity-20" style={{ background: '#C47A5E' }} aria-hidden="true" />
                <div className="relative text-center mb-8">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#F2C4A9] mb-4 flex items-center justify-center gap-2">
                    <Users className="w-3.5 h-3.5" /> Enterprise
                  </p>
                  <p className="text-xs text-white/70 mb-4">For group practices & clinics with 50+ clinicians</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Custom</span>
                  </div>
                  <p className="text-xs text-white font-medium mt-2 bg-white/15 inline-block px-3 py-1 rounded-full border border-white/20">Volume pricing · dedicated support</p>
                </div>
                <div className="relative space-y-3 mb-8 flex-1">
                  {[
                    'Everything in Professional, plus:',
                    'Multi-provider practice management',
                    'Team roster & clinician invites',
                    'Centralized billing across providers',
                    'Admin controls & role-based access',
                    'Dedicated onboarding specialist',
                    'Priority SLA with 1-hour support',
                    'Custom EHR integrations (Epic, Cerner, Athena)',
                    'SSO / SAML — enforced sign-in',
                    'BAA & custom DPA available',
                    'Usage analytics & compliance reports',
                  ].map((feature, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <Check className={`w-4 h-4 shrink-0 ${i === 0 ? 'text-[#F2C4A9]' : 'text-white'}`} />
                      <span className={`text-sm ${i === 0 ? 'font-semibold text-[#F2C4A9]' : 'text-white/90'}`}>{feature}</span>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => navigate('/contact')}
                  className="relative w-full rounded-full py-3.5 text-sm font-semibold bg-[#C47A5E] text-white hover:bg-[#B36A4E] transition-all shadow-md hover:-translate-y-0.5 hover:shadow-lg flex items-center justify-center gap-2"
                  data-testid="pricing-enterprise-cta"
                >
                  Talk to Sales
                  <ArrowRight className="w-4 h-4" />
                </button>
                <p className="relative text-[11px] text-white/70 text-center mt-3">For practices with more than 50 clinicians</p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─────────────────────────── */}
      <section className="py-24 md:py-32 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C47A5E] mb-3 text-center">What Providers Say</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C1917] tracking-tight mb-4 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Trusted by Mental/Behavioral Health Professionals
            </h2>
            <p className="text-base text-stone-500 max-w-xl mx-auto text-center mb-16">
              Clinicians across private practices and group settings use Plexura AI Scribe to reduce burnout and improve documentation quality.
            </p>
          </FadeIn>

          {/* Featured testimonial */}
          <FadeIn>
            <div className="bg-[#F9F6F0] rounded-3xl border border-stone-200 p-8 md:p-12 mb-8 relative overflow-hidden" data-testid="testimonial-featured">
              <div className="absolute top-6 right-8 text-[120px] leading-none text-[#2C4A3B]/[0.06] font-serif pointer-events-none select-none">&ldquo;</div>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 items-center relative">
                <div>
                  <p className="text-lg md:text-xl text-[#1C1917] leading-relaxed mb-6 font-medium" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    Plexura AI Scribe has completely transformed how I document sessions. I used to spend 45 minutes after each patient writing notes. Now my SOAP notes, diagnosis, and billing codes are ready before I even finish my post-session coffee. The speaker diarization is remarkably accurate.
                  </p>
                  <div className="flex items-center gap-4">
                    <img src="https://images.unsplash.com/photo-1612872513575-7e7666b96ff3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHx0aGVyYXBpc3QlMjBtZW50YWwlMjBoZWFsdGglMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzYzMjkwNTh8MA&ixlib=rb-4.1.0&q=85&w=200" alt="Dr. Sarah Mitchell" className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow" />
                    <div>
                      <p className="text-sm font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>Dr. Sarah Mitchell, PsyD</p>
                      <p className="text-xs text-stone-500">Clinical Psychologist &middot; Private Practice, Atlanta</p>
                    </div>
                  </div>
                </div>
                <div className="hidden md:flex flex-col items-center gap-1 px-6 border-l border-stone-200/80">
                  <p className="text-4xl font-bold text-[#2C4A3B]" style={{ fontFamily: "'Outfit', sans-serif" }}>2hrs</p>
                  <p className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold text-center">Saved Daily</p>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Testimonial grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-5">
            {[
              {
                quote: "The CBT and DBT templates are a game-changer. Every session automatically maps to the right documentation structure and billing codes. I no longer worry about missed CPT codes or incomplete notes.",
                name: "Dr. James Okonkwo, LCSW",
                role: "Licensed Clinical Social Worker, Chicago",
                img: "https://images.unsplash.com/photo-1756699197173-5ef672a423fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHx0aGVyYXBpc3QlMjBtZW50YWwlMjBoZWFsdGglMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzYzMjkwNTh8MA&ixlib=rb-4.1.0&q=85&w=200",
                metric: "100%",
                metricLabel: "Billing Accuracy",
                initials: "JO"
              },
              {
                quote: "I run a group practice with 6 therapists. The universal EHR extension means everyone can push notes to SimplePractice without any extra training. It just detected our EHR and worked instantly.",
                name: "Dr. Rachel Nguyen, PhD",
                role: "Practice Owner, Portland",
                img: "https://images.unsplash.com/photo-1734365294772-2f59212083d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHx0aGVyYXBpc3QlMjBtZW50YWwlMjBoZWFsdGglMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzYzMjkwNTh8MA&ixlib=rb-4.1.0&q=85&w=200",
                metric: "6",
                metricLabel: "Therapists Onboard",
                initials: "RN"
              },
              {
                quote: "As a psychiatrist doing medication management, the ICD-10 codes and DSM-5 diagnoses are remarkably thorough. The AI catches differential diagnoses I might not have documented as completely on my own.",
                name: "Dr. Michael Adler, MD",
                role: "Psychiatrist, New York",
                img: null,
                metric: "3x",
                metricLabel: "Faster Charting",
                initials: "MA"
              },
              {
                quote: "Between seeing 20+ patients a day in primary care and managing their behavioral health needs, documentation was drowning me. Now I record the visit, and my notes are done before the next patient walks in. The med management template is perfectly tuned for FNP workflows.",
                name: "Angela Torres, FNP-BC",
                role: "Family Nurse Practitioner, Houston",
                img: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxudXJzZSUyMHByYWN0aXRpb25lciUyMGhlYWx0aGNhcmUlMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzYzMjk5NzZ8MA&ixlib=rb-4.1.0&q=85&w=200",
                metric: "20+",
                metricLabel: "Patients / Day",
                initials: "AT"
              },
            ].map((t, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div className="bg-[#F9F6F0] rounded-3xl border border-stone-200 p-7 h-full flex flex-col justify-between hover:shadow-md transition-shadow" data-testid={`testimonial-${i}`}>
                  <div>
                    <div className="text-3xl text-[#C47A5E]/40 font-serif mb-3 leading-none">&ldquo;</div>
                    <p className="text-sm text-stone-600 leading-relaxed mb-6">{t.quote}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-4 py-3 px-4 bg-white rounded-xl border border-stone-100">
                      <span className="text-xl font-bold text-[#2C4A3B]" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.metric}</span>
                      <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">{t.metricLabel}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {t.img ? (
                        <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#2C4A3B] flex items-center justify-center text-white text-xs font-bold">{t.initials}</div>
                      )}
                      <div>
                        <p className="text-xs font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.name}</p>
                        <p className="text-[10px] text-stone-400">{t.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>

          {/* Second row — 3 more testimonials */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              {
                quote: "I specialize in treatment-resistant depression and complex PTSD. The psychotherapy notes capture nuances I'd normally spend 30 minutes writing — interventions used, patient affect, risk assessment — all structured exactly how my compliance team wants them.",
                name: "Denise Carlson, PMHNP-BC",
                role: "Psychiatric Mental Health NP, Denver",
                img: "https://images.unsplash.com/photo-1670191247079-f9713ae06dcf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxudXJzZSUyMHByYWN0aXRpb25lciUyMGhlYWx0aGNhcmUlMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzYzMjk5NzZ8MA&ixlib=rb-4.1.0&q=85&w=200",
                metric: "30min",
                metricLabel: "Saved Per Note",
                initials: "DC"
              },
              {
                quote: "As a family medicine doctor who also handles anxiety and depression screenings, the auto-generated billing codes have eliminated claim rejections entirely. The system knows when to apply 90834 vs 99214 with the add-on 90833. Revenue went up and admin time went down.",
                name: "Dr. Patrick Reeves, MD",
                role: "Family Medicine Physician, Nashville",
                img: null,
                metric: "0",
                metricLabel: "Claim Rejections",
                initials: "PR"
              },
              {
                quote: "My PMHNP practice sees adolescents with ADHD, anxiety, and mood disorders. The intake template captures developmental history, school performance, and medication trials perfectly. Parents love that I'm more present during visits instead of typing into my laptop.",
                name: "Keisha Williams, PMHNP-BC",
                role: "Psychiatric Mental Health NP, Miami",
                img: "https://images.unsplash.com/photo-1677195063105-276fd4b95b21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxudXJzZSUyMHByYWN0aXRpb25lciUyMGhlYWx0aGNhcmUlMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzYzMjk5NzZ8MA&ixlib=rb-4.1.0&q=85&w=200",
                metric: "95%",
                metricLabel: "Patient Satisfaction",
                initials: "KW"
              },
            ].map((t, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <div className="bg-[#F9F6F0] rounded-3xl border border-stone-200 p-7 h-full flex flex-col justify-between hover:shadow-md transition-shadow" data-testid={`testimonial-${i + 4}`}>
                  <div>
                    <div className="text-3xl text-[#C47A5E]/40 font-serif mb-3 leading-none">&ldquo;</div>
                    <p className="text-sm text-stone-600 leading-relaxed mb-6">{t.quote}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-4 py-3 px-4 bg-white rounded-xl border border-stone-100">
                      <span className="text-xl font-bold text-[#2C4A3B]" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.metric}</span>
                      <span className="text-[10px] uppercase tracking-wider text-stone-400 font-semibold">{t.metricLabel}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {t.img ? (
                        <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[#2C4A3B] flex items-center justify-center text-white text-xs font-bold">{t.initials}</div>
                      )}
                      <div>
                        <p className="text-xs font-bold text-[#1C1917]" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.name}</p>
                        <p className="text-[10px] text-stone-400">{t.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FINAL CTA ───────────────────────────── */}
      <section className="py-24 md:py-32 bg-[#2C4A3B]" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-5" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Reclaim Your Clinical Time
            </h2>
            <p className="text-base text-stone-300 max-w-lg mx-auto mb-10">
              Join mental/behavioral health providers who spend less time on paperwork and more time with patients.
            </p>
            <button
              onClick={goTrial}
              className="rounded-full px-8 py-4 text-sm font-semibold bg-white text-[#2C4A3B] hover:bg-stone-50 transition-all shadow-lg hover:-translate-y-1 hover:shadow-xl inline-flex items-center gap-2"
              data-testid="final-cta"
            >
              Start Your Free 7-Day Trial
              <ChevronRight className="w-4 h-4" />
            </button>
          </FadeIn>
        </div>
      </section>

      {/* ─── FOOTER ──────────────────────────────── */}
      <footer className="py-16 md:py-24 bg-[#1C1917]" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-12">
            <div>
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-xl bg-[#2C4A3B] flex items-center justify-center">
                  <Mic className="w-4 h-4 text-white" />
                </div>
                <span className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Plexura AI Scribe</span>
              </div>
              <p className="text-sm text-stone-500 max-w-xs">AI-powered clinical documentation platform for mental/behavioral health practices. HIPAA compliant.</p>
            </div>
            <div className="flex gap-12">
              <div>
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Product</p>
                <div className="space-y-2">
                  {['Features', 'Templates', 'EHR Integration', 'Pricing'].map(l => (
                    <a key={l} href={`#${l.toLowerCase().replace(/\s/g, '-')}`} className="block text-sm text-stone-500 hover:text-white transition-colors">{l}</a>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Legal</p>
                <div className="space-y-2">
                  {[
                    { label: 'Privacy Policy', to: '/legal/privacy', testid: 'footer-link-privacy' },
                    { label: 'Terms of Service', to: '/legal/terms', testid: 'footer-link-terms' },
                    { label: 'HIPAA Compliance', to: '/legal/hipaa', testid: 'footer-link-hipaa' },
                  ].map(l => (
                    <Link
                      key={l.label}
                      to={l.to}
                      className="block text-sm text-stone-500 hover:text-[#2C4A3B] transition-colors"
                      data-testid={l.testid}
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-stone-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs text-stone-600">&copy; {new Date().getFullYear()} Plexura AI Scribe. All rights reserved.</p>
            <p className="text-xs text-stone-600">HIPAA Compliant &middot; Encrypted &middot; Secure</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
